import React, { useState } from 'react';
import Modal from '../Modal';
import { AnnouncementData } from '../../types/notification';

interface AnnouncementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (data: AnnouncementData) => Promise<void>;
  isSending?: boolean;
}

/**
 * 전체공지 발송 모달 컴포넌트
 *
 * @param isOpen 모달 열림 상태
 * @param onClose 모달 닫기 콜백
 * @param onSend 공지 발송 콜백
 * @param isSending 발송 중 상태 (선택적)
 */
const AnnouncementModal: React.FC<AnnouncementModalProps> = ({
  isOpen,
  onClose,
  onSend,
  isSending = false
}) => {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [titleEn, setTitleEn] = useState('');
  const [messageEn, setMessageEn] = useState('');

  /**
   * 모달 닫기 및 폼 초기화
   */
  const handleClose = () => {
    setTitle('');
    setMessage('');
    setTitleEn('');
    setMessageEn('');
    onClose();
  };

  /**
   * 전체공지 전송
   */
  const handleSend = async () => {
    if (!title.trim() || !message.trim() || !titleEn.trim() || !messageEn.trim()) {
      alert('한국어와 영어 제목/내용을 모두 입력해주세요.');
      return;
    }

    const data: AnnouncementData = {
      title: title.trim(),
      message: message.trim(),
      titleEn: titleEn.trim(),
      messageEn: messageEn.trim()
    };

    await onSend(data);
    handleClose();
  };

  /**
   * 폼 유효성 검사
   */
  const isFormValid = title.trim() && message.trim() && titleEn.trim() && messageEn.trim();

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="전체공지 전송"
      size="lg"
      width="900px"
    >
      <div className="announcement-form">
        <div className="form-group">
          <label htmlFor="announcementTitle">공지 제목 (한국어) *</label>
          <input
            type="text"
            id="announcementTitle"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="form-input"
            placeholder="공지 제목을 입력하세요..."
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="announcementTitleEn">공지 제목 (영어) *</label>
          <input
            type="text"
            id="announcementTitleEn"
            value={titleEn}
            onChange={(e) => setTitleEn(e.target.value)}
            className="form-input"
            placeholder="Notice title in English..."
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="announcementMessage">공지 내용 (한국어) *</label>
          <textarea
            id="announcementMessage"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="form-textarea"
            rows={6}
            placeholder="공지 내용을 입력하세요...&#10;줄바꿈을 사용할 수 있습니다."
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="announcementMessageEn">공지 내용 (영어) *</label>
          <textarea
            id="announcementMessageEn"
            value={messageEn}
            onChange={(e) => setMessageEn(e.target.value)}
            className="form-textarea"
            rows={6}
            placeholder="Notice content in English...&#10;You can use line breaks."
            required
          />
        </div>

        <div className="announcement-info">
          <div className="info-box">
            <h4>전체공지 안내</h4>
            <ul>
              <li>모든 회원에게 동시에 전송됩니다.</li>
              <li>한국어와 영어로 각각 입력해주세요.</li>
              <li>전송 후 수정/삭제가 불가능합니다.</li>
              <li>중요한 공지사항만 전송해주세요.</li>
            </ul>
          </div>
        </div>

        <div className="modal-actions">
          <button
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={isSending}
          >
            취소
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSend}
            disabled={isSending || !isFormValid}
          >
            {isSending ? '전송 중...' : '전체공지 전송'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AnnouncementModal;
